// Glymtober — the app's yearly October event. Currently just the elevated
// daily login bonus + welcome modal; kept as one small shared source of
// truth so both the reward math (daily-login-check route) and the modal's
// own gating agree on the same window and amounts.

/** Standard daily Gooncoin login bonus, outside the holiday window. */
export const STANDARD_DAILY_GOONCOIN = 5

/** Elevated daily Gooncoin login bonus during Glymtober. */
export const GLYMTOBER_DAILY_GOONCOIN = 15

export interface GlymtoberConfig {
  enabled: boolean
  /** 1-12 */
  startMonth: number
  /** 1-31 */
  startDay: number
  /** 1-12 */
  endMonth: number
  /** 1-31 */
  endDay: number
}

/** October 1st through 31st — the calendar default, used before the admin
 * config has loaded and by any caller that doesn't want a DB round trip.
 * Admin-editable via /api/admin/glymtober-config (see
 * components/admin/glymtober-schedule-editor.tsx). */
export const DEFAULT_GLYMTOBER_CONFIG: GlymtoberConfig = {
  enabled: true,
  startMonth: 10,
  startDay: 1,
  endMonth: 10,
  endDay: 31,
}

/** Pure date-window check against an explicit config — handles a window that
 * wraps the new year (e.g. Dec 20 - Jan 5), not just same-month ranges. */
export function isGlymtoberActiveWithConfig(config: GlymtoberConfig, date: Date = new Date()): boolean {
  if (!config.enabled) return false
  const cur = (date.getMonth() + 1) * 100 + date.getDate()
  const start = config.startMonth * 100 + config.startDay
  const end = config.endMonth * 100 + config.endDay
  return start <= end ? cur >= start && cur <= end : cur >= start || cur <= end
}

/** October 1st through 31st, inclusive, any year — the pure calendar
 * default (no DB access). Prefer isGlymtoberActiveWithConfig + a fetched
 * GlymtoberConfig wherever the admin-configured window should apply. */
export function isGlymtoberActive(date: Date = new Date()): boolean {
  return isGlymtoberActiveWithConfig(DEFAULT_GLYMTOBER_CONFIG, date)
}

export function currentDailyGooncoinAmountForConfig(config: GlymtoberConfig, date: Date = new Date()): number {
  return isGlymtoberActiveWithConfig(config, date) ? GLYMTOBER_DAILY_GOONCOIN : STANDARD_DAILY_GOONCOIN
}

export function currentDailyGooncoinAmount(date: Date = new Date()): number {
  return currentDailyGooncoinAmountForConfig(DEFAULT_GLYMTOBER_CONFIG, date)
}

const GLYMTOBER_SETTING_KEY = "glymtober_holiday_config"

/** Server-side read of the admin-configured window (falls back to the
 * calendar default on any error or if never configured). Shared by the admin
 * GET route and daily-login-check, so both agree on the same fallback. */
export async function fetchGlymtoberConfig(supabase: { from: (table: string) => any }): Promise<GlymtoberConfig> {
  try {
    const { data } = await supabase
      .from("admin_settings")
      .select("setting_value")
      .eq("setting_key", GLYMTOBER_SETTING_KEY)
      .single()
    return { ...DEFAULT_GLYMTOBER_CONFIG, ...(data?.setting_value ?? {}) }
  } catch {
    return DEFAULT_GLYMTOBER_CONFIG
  }
}

export const GLYMTOBER_CONFIG_SETTING_KEY = GLYMTOBER_SETTING_KEY
